import React, { useState, useEffect, useMemo } from 'react';
import { Navbar, Nav } from 'react-bootstrap';
import { getFormatedDate, getLastMonthDate, isStringAValidDate } from 'lib/util';
import './Order.scss';
import _ from 'lodash';
import { AppState, dataActions, useUser, useUserParam } from 'reducers';
import { useSelector } from 'react-redux';
import { getOrdersList } from 'lib/communication/orders';
import { OrderDirection, OrderStatusName, OrderStatusNameToLabel, OrderStatusNames } from 'lib/types';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { SortButtonsValue } from 'components/SortButtons/SortButtons';
import store from 'store';
import OrderStatus from './OrderStatus';
import FilterView from 'components/FilterView/FilterView';
import TableView from 'components/TableView/TableView';
import ScrollUpButton from 'components/ScrollUpButton';
import { ORD_PAGE_COUNT } from 'services';
import { useDevice } from 'lib/hooks/useDevice';

export type OrdersState = {
    orderNumber: string;
    customerOrderNumber: string;
    from: string;
    to: string;
    description: string;
    statusFilter: { [NAME in OrderStatusName]: boolean };
    orderBy: string;
    orderDirection: OrderDirection;
    pageSize: number;
};

const ORDER_STATE: OrdersState = {
    orderNumber: '',
    customerOrderNumber: '',
    from: getFormatedDate(new Date(getLastMonthDate())),
    to: getFormatedDate(new Date()),
    description: '',
    statusFilter: Object.fromEntries(_.map(OrderStatusNames, (name) => [name, false])) as { [NAME in OrderStatusName]: boolean },
    orderBy: 'date',
    orderDirection: OrderDirection.Desc,
    pageSize: 10
};

const Orders = () => {
    const history = useHistory();
    const location = useLocation();
    const { page } = useParams<{ page?: string }>();

    const pageSizeParam = useUserParam(ORD_PAGE_COUNT) ?? '10';
    const [ordersState, setOrdersState] = useState<OrdersState>((location.state as OrdersState) || { ...ORDER_STATE, pageSize: pageSizeParam });

    useEffect(() => {
        if (location.state) {
            const newOrdersState: OrdersState = location.state as OrdersState;
            if (!isStringAValidDate(newOrdersState.from)) newOrdersState.from = getFormatedDate(new Date(getLastMonthDate()));
            if (!isStringAValidDate(newOrdersState.to)) newOrdersState.to = getFormatedDate(new Date());

            setOrdersState(newOrdersState);
        }
    }, [location.state]);

    const orders = useSelector((state: AppState) => state.data.orders.orders);
    const isFetchingOrders = useSelector((state: AppState) => state.data.orders.props.isFetchingOrders);
    const showOrderStatus = useSelector((state: AppState) => state.data.orders.orderStatusInfo.show);

    const user = useUser();
    const { isDesktop } = useDevice();

    const [error, setError] = useState(false);

    const [orderNumber, setOrderNumber] = useState('');
    const [customerOrderNumber, setCustomerOrderNumber] = useState('');
    const [from, setFrom] = useState(getFormatedDate(new Date(getLastMonthDate())));
    const [to, setTo] = useState(getFormatedDate(new Date()));
    const [description, setDescription] = useState('');

    const [statusFilter, setStatusFilter] = useState(Object.fromEntries(_.map(OrderStatusNames, (name) => [name, false])) as { [NAME in OrderStatusName]: boolean });
    const statusFilterCount = useMemo(() => _.reduce(statusFilter, (acc, val) => { acc += val ? 1 : 0; return acc; }, 0), [statusFilter]);
    const [openedMobile, setOpenedMobile] = useState([false]);

    const replaceStatusFilter = (status: OrderStatusName) => Object.fromEntries(_.map(OrderStatusNames, (name) => (name === status ? [name, true] : [name, false]))) as { [NAME in OrderStatusName]: boolean };

    const refreshOrdersList = () => {
        if (!isFetchingOrders) {
            _.forEach(_.pickBy(ordersState.statusFilter, (value) => value === true), (value, status) => {
                getOrdersList(
                    status as OrderStatusName,
                    ordersState.orderNumber,
                    ordersState.customerOrderNumber,
                    ordersState.from,
                    ordersState.to,
                    ordersState.description
                ).then((result) => setError(!result));
            });
        }
    };

    useEffect(() => {
        // if from desktop navbar
        if (!page) {
            let defaultOrderStatus: OrderStatusName = 'accepted';
            if (user?.authEnabled) defaultOrderStatus = 'unauthorized';

            history.replace(`${process.env.PUBLIC_URL}/orders/${defaultOrderStatus}`, {
                ...ordersState,
                statusFilter: replaceStatusFilter(defaultOrderStatus as OrderStatusName),
                orderNumber,
                customerOrderNumber,
                description
            });
            return;
        }

        const isSynced = ordersState.statusFilter[page as OrderStatusName] === true;

        if (!isSynced && OrderStatusNames.includes(page as any)) {
            history.replace(`${process.env.PUBLIC_URL}/orders/${page}`, {
                ...ordersState,
                statusFilter: replaceStatusFilter(page as OrderStatusName),
                orderNumber,
                customerOrderNumber,
                description
            });
        }
    }, [page, ordersState.statusFilter, orderNumber, customerOrderNumber, description]);

    // orders page initial load effect
    useEffect(() => {
        store.dispatch(dataActions.clearLocalCart());
    }, []);

    // on page navigation
    useEffect(() => {
        setOrderNumber(ordersState.orderNumber);
        setCustomerOrderNumber(ordersState.customerOrderNumber);
        if (isStringAValidDate(ordersState.from)) setFrom(ordersState.from);
        if (isStringAValidDate(ordersState.to)) setTo(ordersState.to);
        setDescription(ordersState.description);
        setStatusFilter(ordersState.statusFilter);
    }, [ordersState]);

    useEffect(() => {
        refreshOrdersList();
    }, [JSON.stringify(ordersState.statusFilter), ordersState.orderNumber, ordersState.customerOrderNumber, ordersState.from, ordersState.to, ordersState.description]);

    const handleSortChange = (value: SortButtonsValue) => {
        if (value.group && value.direction) {
            setOpenedMobile([false]);
            const urlSuffix = page ? `/${page}` : '';
            history.push(`${process.env.PUBLIC_URL}/orders${urlSuffix}`, {
                ...ordersState,
                orderBy: value.group,
                orderDirection: value.direction,
                orderNumber,
                customerOrderNumber,
                description
            });
        }
    };

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        setOpenedMobile([false]);

        const urlSuffix = page ? `/${page}` : '';

        history.push(`${process.env.PUBLIC_URL}/orders${urlSuffix}`, {
            ...ordersState,
            orderNumber,
            customerOrderNumber,
            description,
            from,
            to
        });
    };

    return (
        <>
            {showOrderStatus && <OrderStatus />}
            <div className={`${isDesktop ? 'Navbar-dektop' : 'Navbar-mobile'}`}>
                <Navbar className='sub-navbar flex-grow-1' expand='lg'>
                    <Nav justify variant='pills' className='flex-grow-1'>
                        {_.map(OrderStatusNameToLabel, (name, status) => {
                            if (status === 'unauthorized' && !user?.authEnabled) return;
                            return (
                                <Nav.Item key={status}>
                                    <Nav.Link
                                        active={statusFilter[status as OrderStatusName] && statusFilterCount === 1}
                                        onClick={() => {
                                            if (!isFetchingOrders) {
                                                history.push(`${process.env.PUBLIC_URL}/orders/${status}`, {
                                                    ...ordersState,
                                                    statusFilter: replaceStatusFilter(status as OrderStatusName),
                                                    orderNumber,
                                                    customerOrderNumber,
                                                    description
                                                });
                                            }
                                        }}
                                        color='#000'
                                    >
                                        {name}
                                    </Nav.Link>
                                </Nav.Item>
                            );
                        })}
                    </Nav>
                </Navbar>
            </div>
            <FilterView handleSearchSubmit={handleSearchSubmit} orderNumber={orderNumber} setOrderNumber={setOrderNumber} customerOrderNumber={customerOrderNumber} setCustomerOrderNumber={setCustomerOrderNumber} from={from} setFrom={setFrom} to={to} setTo={setTo} description={description} setDescription={setDescription} ordersState={ordersState} />
            <TableView openedMobile={openedMobile} handleRefreshOrderList={refreshOrdersList} setOpenedMobile={setOpenedMobile} handleSortChange={handleSortChange} ordersState={ordersState} error={error} orderNumber={orderNumber} customerOrderNumber={customerOrderNumber} description={description} from={from} to={to} orders={orders} />
            <ScrollUpButton />
        </>
    );
};

export default Orders;
